import { Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';

interface ComicSectionProps {
  title: string;
  subtitle?: string;
  comics: typeof import('@/mocks/comics').comics;
  viewMoreLink?: string;
}

export default function ComicSection({ title, subtitle, comics: comicList, viewMoreLink }: ComicSectionProps) {
  return (
    <section className="py-14 md:py-20">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        {/* Header */}
        <div className="flex items-end justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">
              {title}
            </h2>
            {subtitle && (
              <p className="text-foreground-600 mt-2 text-sm">{subtitle}</p>
            )}
          </div>
          {viewMoreLink && (
            <Link
              to={viewMoreLink}
              className="flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>

        {/* Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-5">
          {comicList.map((comic) => (
            <Link
              key={comic.id}
              to={`/comic/${comic.id}`}
              className="group"
              data-product-shop
            >
              {/* Cover */}
              <div className="relative aspect-[3/4] rounded-xl overflow-hidden bg-background-200 mb-3">
                <img
                  src={comic.cover}
                  alt={comic.title}
                  title={`${comic.title} - 琉璃漫站正版漫画`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                {/* Status Badge */}
                <div className={`absolute top-2 left-2 px-2 py-0.5 rounded-md text-xs font-medium ${
                  comic.status === '连载中'
                    ? 'bg-accent-500 text-background-50'
                    : 'bg-foreground-700 text-background-50'
                }`}>
                  {comic.status}
                </div>
                {/* Rating */}
                <div className="absolute bottom-2 right-2 flex items-center gap-1 px-2 py-0.5 rounded-md bg-black/60 text-background-50 text-xs">
                  <i className="ri-star-fill text-accent-400 text-xs"></i>
                  {comic.rating}
                </div>
              </div>

              {/* Info */}
              <h3 className="font-heading font-bold text-foreground-900 text-sm group-hover:text-primary-600 transition-colors line-clamp-1">
                {comic.title}
              </h3>
              <div className="flex items-center gap-2 mt-1.5">
                <span className="text-xs text-foreground-500">{comic.author}</span>
                <span className="text-xs text-foreground-400">·</span>
                <span className="text-xs text-foreground-500">{comic.subcategory}</span>
              </div>
              {comic.updatedAt && (
                <p className="text-xs text-foreground-400 mt-1">
                  更新至 {comic.chapters} 话
                </p>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}